/**
 * Enhanced Argentina Province Detection System
 * Uses multiple detection methods including Google Analytics, IP geolocation, and browser data
 */

// Amplitude tracking removed

// Cache for province detection to prevent excessive loops
let provinceCache: { province: string | null; timestamp: number } | null = null;
const PROVINCE_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Argentina Province Codes and Names
export const ARGENTINA_PROVINCES = {
  // Major Provinces with timezone support
  "BA": { name: "Buenos Aires", timezone: "America/Argentina/Buenos_Aires", population: 17594428 },
  "CB": { name: "Córdoba", timezone: "America/Argentina/Cordoba", population: 3760450 },
  "MZ": { name: "Mendoza", timezone: "America/Argentina/Mendoza", population: 1990338 },
  "TM": { name: "Tucumán", timezone: "America/Argentina/Tucuman", population: 1687305 },
  "SA": { name: "Salta", timezone: "America/Argentina/Salta", population: 1440672 },
  "JY": { name: "Jujuy", timezone: "America/Argentina/Jujuy", population: 797955 },
  "SJ": { name: "San Juan", timezone: "America/Argentina/San_Juan", population: 818234 },
  "SL": { name: "San Luis", timezone: "America/Argentina/San_Luis", population: 508328 },
  "CT": { name: "Catamarca", timezone: "America/Argentina/Catamarca", population: 429556 },
  "LR": { name: "La Rioja", timezone: "America/Argentina/La_Rioja", population: 384607 },
  "TF": { name: "Tierra del Fuego", timezone: "America/Argentina/Ushuaia", population: 190641 },
  
  // Additional provinces (use Buenos Aires timezone as fallback)
  "SF": { name: "Santa Fe", timezone: "America/Argentina/Buenos_Aires", population: 3397532 },
  "ER": { name: "Entre Ríos", timezone: "America/Argentina/Buenos_Aires", population: 1426426 },
  "CR": { name: "Corrientes", timezone: "America/Argentina/Buenos_Aires", population: 1197553 },
  "MI": { name: "Misiones", timezone: "America/Argentina/Buenos_Aires", population: 1280960 },
  "CH": { name: "Chaco", timezone: "America/Argentina/Buenos_Aires", population: 1204541 },
  "FO": { name: "Formosa", timezone: "America/Argentina/Buenos_Aires", population: 606041 },
  "ST": { name: "Santiago del Estero", timezone: "America/Argentina/Buenos_Aires", population: 978313 },
  "RN": { name: "Río Negro", timezone: "America/Argentina/Buenos_Aires", population: 747610 },
  "NQ": { name: "Neuquén", timezone: "America/Argentina/Buenos_Aires", population: 664057 },
  "CHU": { name: "Chubut", timezone: "America/Argentina/Buenos_Aires", population: 618994 },
  "SC": { name: "Santa Cruz", timezone: "America/Argentina/Buenos_Aires", population: 374756 },
  "LP": { name: "La Pampa", timezone: "America/Argentina/Buenos_Aires", population: 358428 }
} as const;

/**
 * Enhanced province detection using multiple methods
 */
export async function detectArgentinaProvinceEnhanced(): Promise<string | null> {
  if (typeof window === "undefined") return null;

  // Check cache first to prevent excessive loops
  const now = Date.now();
  if (provinceCache && (now - provinceCache.timestamp) < PROVINCE_CACHE_DURATION) {
    return provinceCache.province;
  }


  // Method 1: Cloudflare detection removed - now handled by server
  // Server will provide province via headers

  // Method 2: Timezone detection (fallback)
  const timezoneProvince = detectProvinceByTimezone();
  if (timezoneProvince) {
    trackProvinceDetection(timezoneProvince, 'timezone');
    
    // Cache the result
    provinceCache = { province: timezoneProvince, timestamp: Date.now() };
    return timezoneProvince;
  }

  // Method 3: Google Analytics Geolocation API
  const gaProvince = await detectProvinceByGoogleAnalytics();
  if (gaProvince) {
    trackProvinceDetection(gaProvince, 'google_analytics');
    return gaProvince;
  }

  // Method 4: Browser geolocation API (DISABLED)
  // const geoProvince = await detectProvinceByGeolocation();
  // if (geoProvince) {
  //   trackProvinceDetection(geoProvince, 'geolocation');
  //   return geoProvince;
  // }

  // Method 5: IP-based detection (fallback)
  const ipProvince = await detectProvinceByIP();
  if (ipProvince) {
    trackProvinceDetection(ipProvince, 'ip_geolocation');
    
    // Cache the result
    provinceCache = { province: ipProvince, timestamp: Date.now() };
    return ipProvince;
  }

  // Method 5: Statistical fallback (Buenos Aires - most populated)
  trackProvinceDetection('BA', 'statistical_fallback');
  
  // Cache the result to prevent loops
  provinceCache = {
    province: 'BA',
    timestamp: Date.now()
  };
  
  return 'BA';
}

/**
 * Method 1: Detect province by timezone
 */
function detectProvinceByTimezone(): string | null {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (import.meta.env.DEV) {
    // intentional: dev-only logging placeholder
  }

  // Direct timezone matches
  for (const [code, province] of Object.entries(ARGENTINA_PROVINCES)) {
    if (timezone === province.timezone) {
      return code;
    }
  }

  // Fallback: if it's Argentina timezone but not specific
  if (timezone.includes("Argentina")) {
    return "BA"; // Default to Buenos Aires
  }

  return null;
}

/**
 * Method 2: Detect province using Google Analytics geolocation
 */
async function detectProvinceByGoogleAnalytics(): Promise<string | null> {
  try {
    // Check if gtag is available
    if (typeof window.gtag === 'undefined') {
      return null;
    }

    return new Promise((resolve) => {
      // Use gtag to get user location data
      window.gtag('event', 'province_detection_request', {
        event_category: 'geolocation',
        event_label: 'argentina_province',
        custom_callback: (data: Record<string, unknown>) => {
          
          // Parse GA location data
          if (data && data.region) {
            const province = mapRegionToProvince(data.region);
            resolve(province);
          } else {
            resolve(null);
          }
        }
      });

      // Timeout after 3 seconds
      setTimeout(() => resolve(null), 3000);
    });
  } catch (error) {
    return null;
  }
}

/**
 * Method 3: Detect province using browser geolocation API
 * DISABLED: Causes permission prompts and blocking messages
 */
async function detectProvinceByGeolocation(): Promise<string | null> {
  // DISABLED: Remove geolocation API calls to prevent permission prompts
  return null;
  
  // Original geolocation code disabled below:
  /*
  try {
    if (!navigator.geolocation) {
      return null;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          
          // Map coordinates to Argentine provinces
          const province = mapCoordinatesToProvince(latitude, longitude);
          resolve(province);
        },
        (error) => {
          resolve(null);
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    });
  } catch (error) {
    return null;
  }
  */
}

/**
 * Method 4: Detect province using IP geolocation services
 */
async function detectProvinceByIP(): Promise<string | null> {
  try {
    // First check if we should even run IP detection
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserLang = navigator.language;
    const langCountry = browserLang.split("-")[1]?.toUpperCase();

    const isArgentineTimezone = timezone.includes("Argentina") || 
                               timezone.includes("Buenos_Aires");
    const isArgentineLanguage = langCountry === "AR";
    
    // Only run IP detection if likely in Argentina
    if (!isArgentineTimezone && !isArgentineLanguage) {
      return null;
    }

    // Use multiple IP geolocation services as fallbacks
    // Note: Some services have rate limits or CORS issues
    const services = [
      'https://ipinfo.io/json', // Most reliable, put first
      // 'https://ipapi.co/json/', // DISABLED: Rate limited and CORS issues
      // 'https://ip-api.com/json/', // DISABLED: Returns 403 Forbidden
    ];

    for (const serviceUrl of services) {
      try {
        const response = await fetch(serviceUrl, { 
          method: 'GET',
          headers: { 'Accept': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // Check if it's Argentina and extract region
          if (data.country_code === 'AR' || data.country === 'Argentina') {
            const province = mapRegionToProvince(data.region || data.region_name || data.state);
            if (province) return province;
          }
        }
      } catch (serviceError) {
        continue;
      }
    }

    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Map region names to province codes
 */
function mapRegionToProvince(regionName: string): string | null {
  if (!regionName) return null;

  const region = regionName.toLowerCase();
  
  // Direct matches
  const regionMap: Record<string, string> = {
    'buenos aires': 'BA',
    'ciudad autónoma de buenos aires': 'BA',
    'caba': 'BA',
    'capital federal': 'BA',
    'córdoba': 'CB',
    'cordoba': 'CB',
    'mendoza': 'MZ',
    'tucumán': 'TM',
    'tucuman': 'TM',
    'salta': 'SA',
    'jujuy': 'JY',
    'san juan': 'SJ',
    'san luis': 'SL',
    'catamarca': 'CT',
    'la rioja': 'LR',
    'tierra del fuego': 'TF',
    'santa fe': 'SF',
    'entre ríos': 'ER',
    'entre rios': 'ER',
    'corrientes': 'CR',
    'misiones': 'MI',
    'chaco': 'CH',
    'formosa': 'FO',
    'santiago del estero': 'ST',
    'río negro': 'RN',
    'rio negro': 'RN',
    'neuquén': 'NQ',
    'neuquen': 'NQ',
    'chubut': 'CH',
    'santa cruz': 'SC',
    'la pampa': 'LP'
  };

  return regionMap[region] || null;
}

/**
 * Map coordinates to Argentine provinces (approximate)
 */
function mapCoordinatesToProvince(lat: number, lng: number): string | null {
  // Approximate coordinate ranges for major Argentine provinces
  const coordinateRanges = [
    { code: 'BA', latMin: -38.5, latMax: -33.0, lngMin: -62.0, lngMax: -56.0 },
    { code: 'CB', latMin: -35.0, latMax: -29.0, lngMin: -66.0, lngMax: -62.0 },
    { code: 'MZ', latMin: -37.0, latMax: -32.0, lngMin: -70.0, lngMax: -66.0 },
    { code: 'TM', latMin: -28.0, latMax: -26.0, lngMin: -66.0, lngMax: -64.0 },
    { code: 'SA', latMin: -26.0, latMax: -22.0, lngMin: -66.0, lngMax: -62.0 },
    { code: 'JY', latMin: -24.5, latMax: -21.5, lngMin: -66.0, lngMax: -64.0 },
    { code: 'SF', latMin: -34.0, latMax: -28.0, lngMin: -62.0, lngMax: -58.0 },
    { code: 'TF', latMin: -55.0, latMax: -52.0, lngMin: -69.0, lngMax: -63.0 }
  ];

  for (const range of coordinateRanges) {
    if (lat >= range.latMin && lat <= range.latMax && 
        lng >= range.lngMin && lng <= range.lngMax) {
      return range.code;
    }
  }

  return null;
}

/**
 * Track province detection for analytics
 */
function trackProvinceDetection(province: string, method: string) {
  const provinceName = ARGENTINA_PROVINCES[province as keyof typeof ARGENTINA_PROVINCES]?.name || province;
  
  // DISABLED: Amplitude tracking to prevent API error loops
  // Track with Amplitude
  // safeTrack('Argentina Province Detected', {
  //   province_code: province,
  //   province_name: provinceName,
  //   detection_method: method,
  //   timestamp: new Date().toISOString(),
  //   user_agent: navigator.userAgent,
  //   timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  // });

  // Track with Google Analytics (keep this as it doesn't cause loops)
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'province_detected', {
      event_category: 'geolocation',
      event_label: `${province}_${method}`,
      custom_parameter_1: provinceName,
      custom_parameter_2: method
    });
  }

}

/**
 * Get province name from code
 */
export function getProvinceName(code: string): string {
  return ARGENTINA_PROVINCES[code as keyof typeof ARGENTINA_PROVINCES]?.name || code;
}

/**
 * Get all provinces
 */
export function getAllProvinces() {
  return ARGENTINA_PROVINCES;
}

export default {
  detectArgentinaProvinceEnhanced,
  getProvinceName,
  getAllProvinces,
  ARGENTINA_PROVINCES
};